/**
 * Utility functions for date formatting and manipulation
 */

/**
 * Formats a date as MM/DD/YYYY — timezone-safe for billing/waybill pages.
 * Extracts parts directly from ISO strings to avoid UTC→local offset shifts.
 */
export const fmtBillingDate = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '—';
};

/**
 * First and last day of the current month as local YYYY-MM-DD strings, for
 * date-input defaults. Built from local parts — toISOString() would shift the
 * dates back a day in UTC+8.
 */
const toLocalYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const currentMonthRange = () => {
  const now = new Date();
  return {
    first: toLocalYmd(new Date(now.getFullYear(), now.getMonth(), 1)),
    last:  toLocalYmd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

/**
 * Formats a date object or ISO string to a localized date string
 * @param {Date|string} date - Date object or ISO string
 * @param {string} [locale='en-US'] - Locale to use for formatting
 * @returns {string} Formatted date string (e.g., "01/15/2023")
 */
// utils/dateUtils.js - Updated version
export const formatDate = (date, format = 'default') => {
  if (!date) return '';
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  switch (format) {
      case 'time':
          return dateObj.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
          });
      case 'short':
          return dateObj.toLocaleDateString('en-US');
      case 'long':
          return dateObj.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
          });
      case 'YYYY-MM-DDTHH:mm':
          // For datetime-local input
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const hours = String(dateObj.getHours()).padStart(2, '0');
          const minutes = String(dateObj.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
      case 'default':
      default:
          return dateObj.toLocaleDateString('en-PH', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
          });
  }
};

export const parseDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

export const isValidDate = (date) => {
  return date && !isNaN(new Date(date).getTime());
};
  
  /**
   * Formats a date to include time
   * @param {Date|string} date - Date object or ISO string
   * @param {string} [locale='en-US'] - Locale to use for formatting
   * @returns {string} Formatted datetime string (e.g., "01/15/2023, 2:30 PM")
   */
  export const formatDateTime = (date, locale = 'en-US') => {
    if (!date) return '';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) return '';
    
    return dateObj.toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  /**
   * Formats a date to ISO string (YYYY-MM-DD)
   * @param {Date|string} date - Date object or ISO string
   * @returns {string} ISO date string (e.g., "2023-01-15")
   */
  export const toISODateString = (date) => {
    if (!date) return '';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) return '';
    
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };
  
  /**
   * Calculates the difference in days between two dates
   * @param {Date|string} date1 - First date
   * @param {Date|string} date2 - Second date
   * @returns {number} Difference in days
   */
  export const dateDiffInDays = (date1, date2) => {
    const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
    const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
    
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;
    
    const diffTime = Math.abs(d2 - d1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Add to utils/dateUtils.js
  export const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'PHP',
        minimumFractionDigits: 2
    }).format(amount || 0);
  };
  
  const dateUtils = {
    formatDate, 
    formatDateTime,
    toISODateString,
    formatCurrency,
    dateDiffInDays
  };
  
  export default dateUtils;
  